/**
 * External clipboard HTML that needs reshaping before the schema parses it —
 * structure the parser can't infer from tags alone. Everything else is left to
 * the schema (it is the sanitizer).
 */

/**
 * A table becomes its rows as lines, cells separated by a space. The schema has
 * no table, and without this every cell's text ran together ("ABC" from cells
 * A, B, C — QA E5). Inline formatting inside cells is kept.
 */
const tablesToLines = (doc: Document) => {
  for (const table of Array.from(doc.querySelectorAll("table")).reverse()) {
    const lines = doc.createDocumentFragment();
    for (const row of Array.from(table.querySelectorAll("tr"))) {
      const line = doc.createElement("p");
      Array.from(row.children)
        .filter((cell) => cell.tagName === "TD" || cell.tagName === "TH")
        .forEach((cell, index) => {
          if (index > 0) line.appendChild(doc.createTextNode(" "));
          // Cell paragraphs flatten into the row's line.
          for (const block of Array.from(cell.querySelectorAll("p, div"))) {
            block.replaceWith(...Array.from(block.childNodes));
          }
          line.append(...Array.from(cell.childNodes));
        });
      if (line.textContent?.trim()) lines.appendChild(line);
    }
    table.replaceWith(lines);
  }
};

/**
 * A code block becomes one paragraph per line. The schema has no <pre>, so its
 * newlines collapsed and the whole block arrived as one paragraph (QA E17).
 * The <code> text is used when there is one, which leaves out the chrome sites
 * put inside the <pre> (ChatGPT's language label and "Copy" button). Leading
 * indentation becomes non-breaking spaces so it survives whitespace collapse.
 */
const codeBlocksToLines = (doc: Document) => {
  for (const pre of Array.from(doc.querySelectorAll("pre"))) {
    const source = pre.querySelector("code") ?? pre;
    const lines = (source.textContent ?? "").replace(/\n+$/, "").split("\n");
    const paragraphs = doc.createDocumentFragment();
    for (const line of lines) {
      const indent = /^[ \t]*/.exec(line)?.[0] ?? "";
      const p = doc.createElement("p");
      p.textContent =
        indent.replace(/\t/g, "    ").replace(/ /g, " ") +
        line.slice(indent.length);
      paragraphs.appendChild(p);
    }
    pre.replaceWith(paragraphs);
  }
};

/**
 * Word footnote and endnote markers are links to anchors in the document they
 * came from (`#_ftn1`); pasted, they became dead links that opened a new tab.
 * Keep the marker text, drop the link.
 */
const unlinkWordNoteMarkers = (doc: Document) => {
  for (const a of Array.from(
    doc.querySelectorAll('a[href^="#_ftn"], a[href^="#_edn"]'),
  )) {
    a.replaceWith(...Array.from(a.childNodes));
  }
};

const MSO_LIST = /mso-list:\s*l(\d+)\s+level(\d+)/i;

/**
 * Word (desktop) writes lists as paragraphs: `<p style="mso-list:l0 level2">`
 * with the marker as text in a `mso-list:Ignore` span, so every item arrived
 * as a separate "1." paragraph (QA E2). Rebuild real nested lists; the marker
 * text decides numbered vs bulleted.
 */
const wordListsToLists = (doc: Document) => {
  const items = Array.from(
    doc.querySelectorAll<HTMLElement>("p[style]"),
  ).filter((p) => MSO_LIST.test(p.getAttribute("style") ?? ""));
  // The open lists, outermost first. A run of items continues while each one
  // sits right after the list built so far (its previous items were moved in)
  // and belongs to the same Word list.
  let stack: HTMLElement[] = [];
  let listId: string | undefined;
  for (const p of items) {
    const mso = MSO_LIST.exec(p.getAttribute("style") ?? "");
    const level = Math.max(Number(mso?.[2]) || 1, 1);
    const marker = p.querySelector('span[style*="mso-list:Ignore"]');
    const ordered = /^\s*\w{1,4}[.)]/.test(marker?.textContent ?? "");
    marker?.remove();

    // A different Word list (`l0` after `l1`) is a new list: a bulleted list
    // right after a numbered one became its items 4 and 5 (QA E2).
    if (
      !stack[0] ||
      p.previousElementSibling !== stack[0] ||
      mso?.[1] !== listId
    ) {
      stack = [];
    }
    listId = mso?.[1];
    stack = stack.slice(0, level);
    // …and so does a different marker kind at the same level.
    if (
      stack.length === level &&
      (stack[level - 1].tagName === "OL") !== ordered
    ) {
      stack = stack.slice(0, level - 1);
    }
    while (stack.length < level) {
      const list = doc.createElement(ordered ? "ol" : "ul");
      const parent = stack[stack.length - 1];
      if (parent) {
        const host =
          parent.lastElementChild ??
          parent.appendChild(doc.createElement("li"));
        host.appendChild(list);
      } else {
        p.before(list);
      }
      stack.push(list);
    }
    const li = doc.createElement("li");
    li.append(...Array.from(p.childNodes));
    stack[stack.length - 1].appendChild(li);
    p.remove();
  }
};

/**
 * Word for the web writes every list item as its own one-item list —
 * `div.ListContainerWrapper > ul|ol > li[data-aria-level]` — so nested items
 * pasted as one flat list (product QA E7). Rebuild the nesting from the
 * level while the wrappers follow each other and keep the same list id.
 */
const wordOnlineListsToLists = (doc: Document) => {
  let stack: HTMLElement[] = [];
  let listId: string | null = null;
  for (const li of Array.from(
    doc.querySelectorAll<HTMLElement>("li[data-aria-level]"),
  )) {
    const wrapper = li.closest(".ListContainerWrapper");
    const source = li.parentElement;
    if (!wrapper || !source || !/^(UL|OL)$/.test(source.tagName)) continue;
    const level = Math.max(Number(li.getAttribute("data-aria-level")) || 1, 1);
    const ordered = source.tagName === "OL";
    const id = li.getAttribute("data-listid");
    if (
      !stack[0] ||
      wrapper.previousElementSibling !== stack[0] ||
      id !== listId
    ) {
      stack = [];
    }
    listId = id;
    stack = stack.slice(0, level);
    if (
      stack.length === level &&
      (stack[level - 1].tagName === "OL") !== ordered
    ) {
      stack = stack.slice(0, level - 1);
    }
    while (stack.length < level) {
      const list = doc.createElement(ordered ? "ol" : "ul");
      const parent = stack[stack.length - 1];
      if (parent) {
        const host =
          parent.lastElementChild ??
          parent.appendChild(doc.createElement("li"));
        host.appendChild(list);
      } else {
        wrapper.before(list);
      }
      stack.push(list);
    }
    stack[stack.length - 1].appendChild(li);
    if (!wrapper.querySelector("li")) wrapper.remove();
  }
};

/**
 * Notion wraps inline code in `<div style="display:inline">`; a div is a
 * block to the parser, so the line broke around the code (product QA E4).
 */
const inlineDivsToSpans = (doc: Document) => {
  for (const div of Array.from(
    doc.querySelectorAll<HTMLElement>(
      'div[style*="display:inline"], div[style*="display: inline"]',
    ),
  )) {
    if (!/display:\s*inline(;|$|\s)/.test(div.getAttribute("style") ?? ""))
      continue;
    const span = doc.createElement("span");
    span.append(...Array.from(div.childNodes));
    div.replaceWith(span);
  }
};

/**
 * Text colours (spec §6.2). Only document editors carry colours the author
 * picked, so only theirs are kept; Slack, web pages and the rest bring their
 * own look, which gives way to the design system. Even in a document, the
 * default text colour (black, Notion's near-black), link blue and code
 * styling are the editor's look, not the author's: Google Docs writes
 * `color:#000000` on every run, which would override the theme.
 */
const DOCUMENT_EDITOR =
  /urn:schemas-microsoft-com:office|class="?(?:Mso|TextRun|OutlineElement)|docs-internal-guid|class="[^"]*\bnotion-|notionvc/i;
const COLOR = /^\s*color\s*:/i;

const rgbOf = (color: string): number[] | null => {
  const hex = color.match(/^#([\da-f]{3}|[\da-f]{6})$/i)?.[1];
  if (hex) {
    const full = hex.length === 3 ? hex.replace(/./g, "$&$&") : hex;
    return [0, 2, 4].map((i) => Number.parseInt(full.slice(i, i + 2), 16));
  }
  const rgb = color.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  return rgb ? rgb.slice(1, 4).map(Number) : null;
};

const isDefaultTextColor = (color: string) => {
  if (/^(black|windowtext|canvastext|auto)$/i.test(color)) return true;
  const rgb = rgbOf(color);
  if (!rgb) return false;
  // Black and the near-black greys editors use for body text.
  const max = Math.max(...rgb);
  return max <= 64 && max - Math.min(...rgb) <= 16;
};

const keepAuthorColors = (doc: Document, fromDocument: boolean) => {
  for (const el of Array.from(doc.querySelectorAll("[style]"))) {
    const declarations = (el.getAttribute("style") ?? "").split(";");
    const color = declarations
      .find((d) => COLOR.test(d))
      ?.replace(COLOR, "")
      .replace(/!important/i, "")
      .trim();
    if (!color) continue;
    const authored =
      fromDocument &&
      !isDefaultTextColor(color) &&
      !el.closest('a, code, pre, [class*="inline-code"]');
    if (authored) continue;
    el.setAttribute(
      "style",
      declarations.filter((d) => !COLOR.test(d)).join(";"),
    );
  }
};

/**
 * Gmail writes a blank line as <div><br></div>. ProseMirror's clipboard parser
 * ignores a <br> that ends a block, so the div parsed to nothing while a web
 * page's <p><br></p> stayed an empty paragraph (QA E16).
 */
const blankDivsToParagraphs = (doc: Document) => {
  for (const div of Array.from(doc.querySelectorAll("div"))) {
    if (div.childNodes.length !== 1 || div.firstChild?.nodeName !== "BR")
      continue;
    const p = doc.createElement("p");
    p.appendChild(doc.createElement("br"));
    div.replaceWith(p);
  }
};

export const normalizeExternalHtml = (html: string): string => {
  if (
    !/<table|<pre|<svg|<div><br\s*\/?><\/div>|mso-list|#_(ftn|edn)|data-aria-level|display:\s*inline|color/i.test(
      html,
    )
  )
    return html;
  const doc = new window.DOMParser().parseFromString(html, "text/html");
  keepAuthorColors(doc, DOCUMENT_EDITOR.test(html));
  // Icons and charts drawn inline: their <text> labels are not prose (QA E3).
  doc.querySelectorAll("svg").forEach((svg) => svg.remove());
  inlineDivsToSpans(doc);
  blankDivsToParagraphs(doc);
  codeBlocksToLines(doc);
  tablesToLines(doc);
  unlinkWordNoteMarkers(doc);
  wordListsToLists(doc);
  wordOnlineListsToLists(doc);
  return doc.body.innerHTML;
};
